/**
 * OaSIS Explorer Frontend - Button Component
 * Task 3.1: Frontend Foundation & Global Features
 * 
 * Reusable button component with theme support
 */

import React from 'react';
import type { ButtonProps } from '../../types';

/**
 * Button component with multiple variants and sizes
 */
export const Button: React.FC<ButtonProps> = ({
  children,
  onClick,
  variant = 'primary',
  size = 'md',
  disabled = false,
  className = '',
}) => {
  const baseClasses = [
    'inline-flex',
    'items-center',
    'justify-center',
    'font-medium',
    'border',
    'rounded-md',
    'transition-all',
    'duration-200',
    'focus:outline-none',
    'focus:ring-2',
    'focus:ring-offset-2',
    'focus:ring-blue-500',
    'disabled:opacity-60',
    'disabled:cursor-not-allowed',
  ];

  const variantClasses = {
    primary: [
      'bg-primary-600',
      'text-white',
      'border-primary-600',
      'hover:bg-primary-700',
      'hover:border-primary-700',
      'active:bg-primary-800',
    ],
    outline: [
      'bg-transparent',
      'text-primary-600',
      'border-primary-600',
      'hover:bg-primary-50',
      'hover:text-primary-700',
      'active:bg-primary-100',
      'dark:text-primary-400',
      'dark:border-primary-400',
      'dark:hover:bg-primary-950',
      'dark:hover:text-primary-300',
      'dark:active:bg-primary-900',
    ],
  };

  const sizeClasses = {
    sm: ['px-3', 'py-1.5', 'text-sm'],
    md: ['px-4', 'py-2', 'text-base'],
    lg: ['px-6', 'py-3', 'text-lg'],
  };

  const classes = [
    ...baseClasses,
    ...variantClasses[variant],
    ...sizeClasses[size],
    className,
  ].join(' ');

  return (
    <button
      type="button"
      className={classes}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled}
    >
      {children}
    </button>
  );
};

